import {
  ADMIN_INCIDENT_MESSAGES,
  ADMIN_PRIVACY_POLICY,
  ADMIN_SETTINGS_WHATS_NEW,
  ADMIN_TERMS_AND_CONDITIONS,
  AdminLogoutModal,
  AdminSpinner,
  AdminSwitch,
  ArrowRed,
  Box,
  Button,
  Div,
  H3,
  H5,
  Icon,
  Logout,
  P,
  SuccessToast,
  TextWithTooltipIcon,
  api,
  getUserById,
  isAdmin,
  lightTheme,
  useDemoMode,
  useAuth,
  useAuthenticatedAuthUser,
  reportError,
} from '@vgrubs/components';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { DeleteAccountModal } from '../DeleteAccountModal';

export const GeneralSettings = () => {
  const [loading, setLoading] = useState(false);
  const [logoutModal, setLogoutModal] = useState(false);
  const [deleteAccountModal, setDeleteAccountModal] = useState(false);

  const { updateNotificationStatus, updateEmailNotificationStatus } = useAuth((state) => state);

  const user = useAuthenticatedAuthUser();
  const role = user?.role;
  const demoMode = useDemoMode();

  async function handleUpdateNotificationStatus() {
    try {
      setLoading(true);
      const { data } = await api.put(`/v1/users/notification-status`, {
        enabled: !user.notificationsEnabled,
      });
      updateNotificationStatus(data.data.notificationsEnabled);
      SuccessToast(data.message);
    } catch (error) {
      reportError(error);
    } finally {
      setLoading(false);
    }
  }

  async function getNotificationsStatus() {
    try {
      setLoading(true);
      const { data } = await getUserById(user.id);
      updateNotificationStatus(data.data.notificationsEnabled ?? false);
      updateEmailNotificationStatus(data.data.emailNotificationsEnabled ?? false);
    } catch (error) {
      reportError(error);
    }
    finally {
      setLoading(false);
    }
  }

  async function handleUpdateEmailNotificationStatus() {
    try {
      setLoading(true);
      const { data } = await api.put(`/v1/users/email-notification-status`, {
        enabled: !user.emailNotificationsEnabled,
      });
      updateEmailNotificationStatus(data.data.emailNotificationsEnabled);
      SuccessToast(data.message);
    }
    catch (error) {
      reportError(error);
    }
    finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    getNotificationsStatus();
  }, []);

  // The link rows, split evenly over the two columns with Logout always last
  // on the right. On admin, What's New moves up beside Enter demo mode so the
  // two switch rows stay paired, which leaves admin and partner both with
  // four rows here (2 + 2). A fixed split left partner with three on the
  // left and Logout alone on the right.
  const whatsNew = { to: ADMIN_SETTINGS_WHATS_NEW, label: "What's New" };
  const links = [
    ...(isAdmin ? [] : [whatsNew]),
    { to: ADMIN_PRIVACY_POLICY, label: 'Privacy Policy' },
    { to: ADMIN_TERMS_AND_CONDITIONS, label: 'Terms & Conditions' },
    ...(isAdmin
      ? [{ to: ADMIN_INCIDENT_MESSAGES, label: 'vDrive Tracking Link' }]
      : []),
  ];
  const leftCount = Math.ceil((links.length + 1) / 2);
  const leftLinks = links.slice(0, leftCount);
  const rightLinks = links.slice(leftCount);

  const renderLink = ({ to, label }: { to: string; label: string }) => (
    <Link to={to} key={to}>
      <Box $flex $alignCenter $between $mb="8">
        <H5 $m="0">{label}</H5>
        <Icon>
          <ArrowRed />
        </Icon>
      </Box>
    </Link>
  );

  return (
    <>
      {loading && <AdminSpinner isRootPage />}

      <H3 $m="32px 0" $mSM="20px 0">
        General Settings
      </H3>

      <Div $flex $gap="24px" $gapSM="0" $columnSM>
        <Div $width="50%" $widthSM="100%">
          <Box $flex $alignCenter $between $mb="8">
            <P $m="0">Enable push notifications</P>
            <AdminSwitch
              checked={user.notificationsEnabled}
              onChange={handleUpdateNotificationStatus}
            />
          </Box>
          {/* Under push notifications, in the same column, so it shares the
              row's width and paints after the box above (a plain block below
              the row would sit under that box's shadow). */}
          {isAdmin && (
            <Box $flex $alignCenter $between $mb="8">
              <TextWithTooltipIcon
                text="Enter demo mode"
                tooltip="Orders and deliveries show every fleet as DSP instead of the provider that delivers, so the app can be shown without revealing our delivery partners. Saved in this browser only."
              />
              <AdminSwitch
                checked={demoMode.enabled}
                onChange={() => demoMode.setEnabled(!demoMode.enabled)}
              />
            </Box>
          )}
        </Div>
        <Div $width="50%" $widthSM="100%">
          <Box $flex $alignCenter $between $mb="8">
            <P $m="0">Enable email notifications</P>
            <AdminSwitch
              checked={user.emailNotificationsEnabled}
              onChange={handleUpdateEmailNotificationStatus}
            />
          </Box>
          {isAdmin && renderLink(whatsNew)}
        </Div>
      </Div>

      <Div $flex $gap="24px" $gapSM="0" $columnSM>
        <Div $width="50%" $widthSM="100%">
          {leftLinks.map(renderLink)}
        </Div>
        <Div $width="50%" $widthSM="100%">
          {rightLinks.map(renderLink)}
          <Box
            $pointer
            $mb="8"
            $flex
            $alignCenter
            $between
            onClick={() => setLogoutModal(true)}
          >
            <H5 $m="0" $color={lightTheme.primary}>
              Logout
            </H5>
            <Icon $color={lightTheme.primary} $size="20">
              <Logout $color={lightTheme.primary} />
            </Icon>
          </Box>
        </Div>
      </Div>

      <Div $m="32px 0" $flex $justifyEnd>
        <Button
          $selected
          $w="300px"
          onClick={() => setDeleteAccountModal(true)}
        >
          Delete Account
        </Button>
      </Div>

      <AdminLogoutModal isOpen={logoutModal} setIsOpen={setLogoutModal} />
      <DeleteAccountModal
        modalIsOpen={deleteAccountModal}
        closeModal={() => setDeleteAccountModal(false)}
      />
    </>
  );
};
